import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ApiURL from "../controllers/Api";
import AdminLayout from "./AdminLayout";
import CompanyLayout from "./CompanyLayout";
import { toast } from "../utils/toast";

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const handleJson = async (response) => {
  const body = await response.json().catch(() => ({}));
  return {
    success: response.ok && body.success !== false,
    body,
    message: body.message || "",
  };
};

const initialForm = {
  name: "",
  email: "",
  mobile: "",
  job_role: "Recruiter",
  city: "",
  state: "",
  address: "",
  password: "Welcome@123",
  can_view_dashboard: true,
  can_upload_resumes: true,
  can_post_jobs: true,
  can_view_resume_library: false,
  can_manage_sub_companies: false,
};

const AssignIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8">
    <path d="M5 10h10M10 5l5 5-5 5" />
  </svg>
);

export default function EmployeeManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCompanyView = localStorage.getItem("userType") === "company";
  const Layout = isCompanyView ? CompanyLayout : AdminLayout;
  const [employees, setEmployees] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState(initialForm);
  const [resumePool, setResumePool] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignEmployee, setAssignEmployee] = useState(null);
  const [selectedResumeIds, setSelectedResumeIds] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [editForm, setEditForm] = useState({
    id: null,
    name: "",
    email: "",
    mobile: "",
    job_role: "",
    department_id: null,
    role_id: null,
    status: "active",
    city: "",
    state: "",
    address: "",
    can_upload_resumes: true,
    can_post_jobs: false,
    can_view_dashboard: true,
    can_view_resume_library: false,
    can_manage_sub_companies: false,
  });

  const taggedResumePool = resumePool.filter(

    (resume) =>
      (resume.sub_company_ids || []).length > 0 &&
      (resume.requirement_ids || []).length > 0 &&
      !resume.is_hired
  );

  const loadEmployees = async () => {
    try {
      const response = await fetch(`${ApiURL}crm/admin/get_employees.php`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to load recruiters");
      }
      setEmployees(result?.data?.employees || result?.employees || []);
    } catch (error) {
      console.error("Error loading recruiters:", error);
      toast.error(error.message || "Failed to load recruiters");
    }
  };

  const loadResumePool = async () => {
    try {
      const response = await fetch(`${ApiURL}crm/admin/list_resume_pool.php`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to load resume pool");
      }
      const resumes = result?.data?.resumes || [];
      setResumePool(resumes);
      return resumes;
    } catch (error) {
      console.error("Error loading resume pool:", error);
      toast.error(error.message || "Failed to load resume pool");
      return [];
    }
  };

  useEffect(() => {
    loadEmployees();
    if (isCompanyView) {
      loadResumePool();
    }
  }, []);

  useEffect(() => {
    setShowCreateForm(location.pathname.endsWith("/create"));
  }, [location.pathname]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const grantAllRecruiterTabs = () => {
    setForm((prev) => ({
      ...prev,
      can_view_dashboard: true,
      can_upload_resumes: true,
      can_post_jobs: true,
      can_view_resume_library: true,
      can_manage_sub_companies: true,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${ApiURL}crm/admin/create_employee.php`, {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(form),
      });

      const result = await handleJson(response);
      if (!result.success) {
        setMessage(result.message || "Failed to create recruiter.");
        toast.error(result.message || "Failed to create recruiter.");
        return;
      }

      const employee = result.body?.data?.employee || result.body?.employee;
      const successMessage = employee
        ? `Recruiter created successfully. Personnel ID: ${employee.personnel_id}`
        : "Recruiter created successfully.";

      setMessage(successMessage);
      toast.success(successMessage);
      setForm(initialForm);
      await loadEmployees();
    } finally {
      setLoading(false);
    }
  };

  const closeCreateForm = () => {
    setShowCreateForm(false);
    setForm(initialForm);
    if (location.pathname.endsWith("/create")) {
      navigate("/company/employees");
    }
  };

  const openAssignModal = async (employee) => {
    const latestResumePool = await loadResumePool();
    const taggedResumes = latestResumePool.filter(
      (resume) =>
        (resume.sub_company_ids || []).length > 0 &&
        (resume.requirement_ids || []).length > 0 &&
        !resume.is_hired
    );
    setAssignEmployee(employee);
    setSelectedResumeIds(
      taggedResumes
        .filter((resume) => (resume.assigned_employee_ids || []).includes(employee.id))
        .map((resume) => resume.id)
    );
    setShowAssignModal(true);
  };

  useEffect(() => {
    if (!showAssignModal) {
      return;
    }

    const visibleIds = new Set(taggedResumePool.map((resume) => resume.id));
    setSelectedResumeIds((prev) => prev.filter((resumeId) => visibleIds.has(resumeId)));
  }, [showAssignModal, taggedResumePool]);

  const closeAssignModal = () => {
    setAssignEmployee(null);
    setSelectedResumeIds([]);
    setShowAssignModal(false);
  };

  const openEditModal = (employee) => {
    setEditEmployee(employee);
    setEditMessage("");
    setEditForm({
      id: employee.id,
      name: employee.name || "",
      email: employee.email || "",
      mobile: employee.mobile || "",
      job_role: employee.job_role || "",
      department_id: employee.department_id ?? null,
      role_id: employee.role_id ?? null,
      status: employee.status === "inactive" ? "inactive" : "active",
      city: employee.city || "",
      state: employee.state || "",
      address: employee.address || "",
      can_view_dashboard: employee.can_view_dashboard !== false,
      can_upload_resumes: employee.can_upload_resumes !== false,
      can_post_jobs: Boolean(employee.can_post_jobs),
      can_view_resume_library: Boolean(employee.can_view_resume_library),
      can_manage_sub_companies: Boolean(employee.can_manage_sub_companies),
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditEmployee(null);
    setEditLoading(false);
    setEditMessage("");
    setEditForm({
      id: null,
      name: "",
      email: "",
      mobile: "",
      job_role: "",
      department_id: null,
      role_id: null,
      status: "active",
      city: "",
      state: "",
      address: "",
      can_view_dashboard: true,
      can_upload_resumes: true,
      can_post_jobs: false,
      can_view_resume_library: false,
      can_manage_sub_companies: false,
    });
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editForm.id) {
      toast.error("Employee id missing");
      return;
    }

    setEditLoading(true);
    setEditMessage("");

    try {
      const response = await fetch(`${ApiURL}crm/admin/update_employee.php`, {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(editForm),
      });

      const result = await handleJson(response);
      if (!result.success) {
        const msg = result.message || "Failed to update recruiter";
        setEditMessage(msg);
        toast.error(msg);
        return;
      }

      // Reload employees first, then close modal to ensure fresh data is fetched before state resets
      await loadEmployees();
      toast.success(result.message || "Recruiter updated successfully");
      closeEditModal();
    } catch (error) {
      console.error("Error updating recruiter:", error);
      const msg = error.message || "Failed to update recruiter";
      setEditMessage(msg);
      toast.error(msg);
    } finally {
      setEditLoading(false);
    }
  };

  const handleBulkAssign = async () => {

    if (!assignEmployee || selectedResumeIds.length === 0) {
      toast.warning("Please select at least one resume");
      return;
    }

    setAssignLoading(true);
    try {
      const response = await fetch(`${ApiURL}crm/admin/bulk_assign_employee.php`, {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          employee_id: assignEmployee.id,
          resume_ids: selectedResumeIds,
        }),
      });

      const result = await handleJson(response);
      if (!result.success) {
        throw new Error(result.message || "Failed to assign resumes");
      }

      toast.success(result.message || "Selected resumes assigned successfully.");
      closeAssignModal();
      await loadResumePool();
    } catch (error) {
      console.error("Error assigning resumes:", error);
      toast.error(error.message || "Failed to assign resumes");
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <Layout
      title="Recruiter Management"
      description={
        isCompanyView
          ? "Manage your company recruiters and create new recruiter accounts from one page."
          : "Manage recruiters and create new recruiter accounts from one page."
      }
      headerAction={
        !showCreateForm ? (
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Add Recruiter
          </button>
        ) : null
      }
    >
      <div className="space-y-4">
        {showCreateForm ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Add Recruiter</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Create recruiter accounts for your company team and assign resumes to them.
                </p>
              </div>
              <button
                type="button"
                onClick={closeCreateForm}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close add recruiter form"
              >
                x
              </button>
            </div>

            {message ? (
              <div className="mt-4 rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-700">
                {message}
              </div>
            ) : null}

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Full Name</label>
                  <input name="name" value={form.name} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" required />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" required />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Phone</label>
                  <input name="mobile" value={form.mobile} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Designation</label>
                  <input name="job_role" value={form.job_role} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Password</label>
                  <input name="password" value={form.password} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">City</label>
                  <input name="city" value={form.city} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">State</label>
                  <input name="state" value={form.state} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Address</label>
                <textarea name="address" value={form.address} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" rows={3} />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-blue-900">Recruiter tab access</div>
                    <div className="text-xs text-blue-700">Tick only the tabs this recruiter should see.</div>
                  </div>
                  <button
                    type="button"
                    onClick={grantAllRecruiterTabs}
                    className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    Enable All
                  </button>
                </div>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                  <input
                    type="checkbox"
                    name="can_view_dashboard"
                    checked={Boolean(form.can_view_dashboard)}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Dashboard</span>
                    <span className="block text-xs text-slate-500">Recruiter can open the dashboard overview.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                  <input
                    type="checkbox"
                    name="can_upload_resumes"
                    checked={Boolean(form.can_upload_resumes)}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Upload resumes</span>
                    <span className="block text-xs text-slate-500">Recruiter uploads go into this company resume pool.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                  <input
                    type="checkbox"
                    name="can_post_jobs"
                    checked={Boolean(form.can_post_jobs)}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Post jobs</span>
                    <span className="block text-xs text-slate-500">Recruiter can publish and manage jobs for this company.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                  <input
                    type="checkbox"
                    name="can_view_resume_library"
                    checked={Boolean(form.can_view_resume_library)}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Resume Library</span>
                    <span className="block text-xs text-slate-500">Recruiter can view, tag, and assign company resumes.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                  <input
                    type="checkbox"
                    name="can_manage_sub_companies"
                    checked={Boolean(form.can_manage_sub_companies)}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Sub Companies</span>
                    <span className="block text-xs text-slate-500">Recruiter can manage client companies and requirements.</span>
                  </span>
                </label>
              </div>
              <div>
                <button type="submit" disabled={loading} className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                  {loading ? "Creating..." : "Create Recruiter"}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Name</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Email</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Phone</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Role</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Permissions</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Personnel ID</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Action</th>
                </tr>
                <tr>
                  <td colSpan={8} className="px-3 py-0">
                    <span className="text-[10px] text-slate-500">Tip: recruiter fields can be updated from this table.</span>
                  </td>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {employees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 font-medium text-slate-900">{employee.name}</td>
                    <td className="px-3 py-3 text-slate-600">{employee.email}</td>
                    <td className="px-3 py-3 text-slate-600">{employee.mobile || "-"}</td>
                    <td className="px-3 py-3 text-slate-600">{employee.role_name || "-"}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {employee.can_upload_resumes !== false ? (
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Upload</span>
                        ) : null}
                        {employee.can_post_jobs ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Jobs</span>
                        ) : null}
                        {employee.can_view_dashboard !== false ? (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">Dashboard</span>
                        ) : null}
                        {employee.can_view_resume_library ? (
                          <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700">Library</span>
                        ) : null}
                        {employee.can_manage_sub_companies ? (
                          <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-700">Sub Companies</span>
                        ) : null}
                        {employee.can_upload_resumes === false && !employee.can_post_jobs && employee.can_view_dashboard === false && !employee.can_view_resume_library && !employee.can_manage_sub_companies ? (
                          <span className="text-[10px] text-slate-400">No extra access</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{employee.personnel_id || "-"}</td>
                    <td className="px-3 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${employee.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {employee.status}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(employee)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-700 hover:bg-blue-50"
                          title="Edit recruiter"
                          aria-label="Edit recruiter"
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                          </svg>
                        </button>

                        {isCompanyView ? (
                          <button
                            type="button"
                            onClick={() => openAssignModal(employee)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-700 hover:bg-blue-50"
                            title="Assign tagged resumes"
                          >
                            <AssignIcon />
                          </button>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No recruiters found.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        {showEditModal && editEmployee ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-[1px]">
            <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-gray-200 bg-white text-gray-900 shadow-2xl">
              <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
                <div>
                  <h3 className="text-2xl font-semibold tracking-tight text-gray-900">Edit Recruiter</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Update recruiter details for <span className="font-medium text-gray-900">{editEmployee.name}</span>.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  x
                </button>
              </div>

              <div className="space-y-4 overflow-y-auto px-6 py-5">
                {editMessage ? (
                  <div className="rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-700">{editMessage}</div>
                ) : null}

                <form className="space-y-4" onSubmit={handleEditSubmit}>
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Full Name</label>
                      <input
                        name="name"
                        value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                      <input
                        name="email"
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Phone</label>
                      <input
                        name="mobile"
                        value={editForm.mobile}
                        onChange={(e) => setEditForm((p) => ({ ...p, mobile: e.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Designation</label>
                      <input
                        name="job_role"
                        value={editForm.job_role}
                        onChange={(e) => setEditForm((p) => ({ ...p, job_role: e.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">City</label>
                      <input
                        name="city"
                        value={editForm.city}
                        onChange={(e) => setEditForm((p) => ({ ...p, city: e.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">State</label>
                      <input
                        name="state"
                        value={editForm.state}
                        onChange={(e) => setEditForm((p) => ({ ...p, state: e.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Status</label>
                      <select
                        name="status"
                        value={editForm.status}
                        onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="active">active</option>
                        <option value="inactive">inactive</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Address</label>
                    <textarea
                      name="address"
                      value={editForm.address}
                      onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))}
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      rows={3}
                    />
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                      <div>
                        <div className="text-sm font-semibold text-blue-900">Recruiter tab access</div>
                        <div className="text-xs text-blue-700">Tick only the tabs this recruiter should see.</div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setEditForm((p) => ({
                            ...p,
                            can_view_dashboard: true,
                            can_upload_resumes: true,
                            can_post_jobs: true,
                            can_view_resume_library: true,
                            can_manage_sub_companies: true,
                          }))
                        }
                        className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                      >
                        Enable All
                      </button>
                    </div>
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={Boolean(editForm.can_view_dashboard)}
                        onChange={(e) => setEditForm((p) => ({ ...p, can_view_dashboard: e.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-800">Dashboard</span>
                        <span className="block text-xs text-slate-500">Recruiter can open the dashboard overview.</span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={Boolean(editForm.can_upload_resumes)}
                        onChange={(e) => setEditForm((p) => ({ ...p, can_upload_resumes: e.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-800">Upload resumes</span>
                        <span className="block text-xs text-slate-500">Uploads are saved only in the parent company pool.</span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={Boolean(editForm.can_post_jobs)}
                        onChange={(e) => setEditForm((p) => ({ ...p, can_post_jobs: e.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-800">Post jobs</span>
                        <span className="block text-xs text-slate-500">Allowed to publish and manage jobs for this company.</span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={Boolean(editForm.can_view_resume_library)}
                        onChange={(e) => setEditForm((p) => ({ ...p, can_view_resume_library: e.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-800">Resume Library</span>
                        <span className="block text-xs text-slate-500">Can view, tag, and assign company resumes.</span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={Boolean(editForm.can_manage_sub_companies)}
                        onChange={(e) => setEditForm((p) => ({ ...p, can_manage_sub_companies: e.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-800">Sub Companies</span>
                        <span className="block text-xs text-slate-500">Can manage client companies and requirements.</span>
                      </span>
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                    <button
                      type="button"
                      onClick={closeEditModal}
                      className="rounded-2xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={editLoading}
                      className="rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {editLoading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : null}

        {showAssignModal && assignEmployee ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-[1px]">
            <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-gray-200 bg-white text-gray-900 shadow-2xl">

              <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
                <div>
                  <h3 className="text-2xl font-semibold tracking-tight text-gray-900">Assign Resumes</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Assign only resumes that already have at least one company tag and one requirement to{" "}
                    <span className="font-medium text-gray-900">{assignEmployee.name}</span>.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeAssignModal}
                  className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  x
                </button>
              </div>

              <div className="space-y-4 overflow-y-auto px-6 py-5">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedResumeIds(taggedResumePool.map((resume) => resume.id))}
                    className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedResumeIds([])}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                  <span className="text-xs text-gray-500">Selected: {selectedResumeIds.length}</span>
                </div>

                <div className="max-h-[420px] overflow-auto rounded-2xl border border-gray-200">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-3 text-left font-medium text-gray-600">Select</th>
                        <th className="px-3 py-3 text-left font-medium text-gray-600">Resume</th>
                        <th className="px-3 py-3 text-left font-medium text-gray-600">Candidate</th>
                        <th className="px-3 py-3 text-left font-medium text-gray-600">Tagged Company</th>
                        <th className="px-3 py-3 text-left font-medium text-gray-600">Requirement</th>
                        <th className="px-3 py-3 text-left font-medium text-gray-600">Assignees</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {taggedResumePool.map((resume) => {
                        const checked = selectedResumeIds.includes(resume.id);
                        return (
                          <tr key={resume.id} className="hover:bg-gray-50 align-top">
                            <td className="px-3 py-3">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) =>
                                  setSelectedResumeIds((prev) =>
                                    event.target.checked
                                      ? [...prev, resume.id]
                                      : prev.filter((item) => item !== resume.id)
                                  )
                                }
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-3 py-3 text-slate-600">
                              <div className="font-medium text-slate-900">#{resume.id}</div>
                              <div className="text-[10px] text-slate-500">{resume.original_filename}</div>
                            </td>
                            <td className="px-3 py-3 text-slate-600">
                              <div>{resume.candidate_name || "-"}</div>
                              <div className="text-[10px] text-slate-500">{resume.candidate_role || ""}</div>
                            </td>
                            <td className="px-3 py-3 text-slate-600">{resume.sub_company_names?.join(", ") || "-"}</td>
                            <td className="px-3 py-3 text-slate-600">{resume.requirement_names?.join(", ") || "-"}</td>
                            <td className="px-3 py-3 text-slate-600">
                              {resume.is_hired
                                ? `Hired${resume.hired_employee_name ? ` by ${resume.hired_employee_name}` : ""}`
                                : resume.assigned_employee_names?.length
                                  ? resume.assigned_employee_names.join(", ")
                                  : "Unassigned"}
                            </td>
                          </tr>
                        );
                      })}
                      {taggedResumePool.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                            No assignable tagged resumes found. Hired resumes are locked automatically.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-5">
                <button
                  type="button"
                  onClick={closeAssignModal}
                  className="rounded-2xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkAssign}
                  disabled={assignLoading}
                  className="rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {assignLoading ? "Assigning..." : "Assign Selected"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}
